/**
 * Share links — a tool's input encoded in the URL fragment:
 *   https://unveil.tools/{toolId}#s={version}{payload}
 *
 * The fragment never reaches the server or analytics. Payload = base64url of
 * the UTF-8 JSON state; version "1" is deflate-compressed (CompressionStream),
 * version "0" is uncompressed (fallback for browsers without it).
 *
 * The state is the same { inputId: value } map sessions use. What is left
 * out (passwords, keys) is decided by sessions.captureState.
 */

const PREFIX = 's=';
// Above this the link becomes unwieldy (and some chat apps truncate it)
export const MAX_SHARE_URL_LENGTH = 8000;

function toBase64Url(bytes) {
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(text) {
  const b64 = text.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

async function pipeThrough(bytes, stream) {
  const out = new Response(new Blob([bytes]).stream().pipeThrough(stream));
  return new Uint8Array(await out.arrayBuffer());
}

/** Fragment text (without "#") for a state object. */
export async function encodeShareFragment(state) {
  const json = new TextEncoder().encode(JSON.stringify(state));
  if (typeof CompressionStream === 'function') {
    try {
      return `${PREFIX}1${toBase64Url(await pipeThrough(json, new CompressionStream('deflate-raw')))}`;
    } catch {
      /* fall through to uncompressed */
    }
  }
  return `${PREFIX}0${toBase64Url(json)}`;
}

/**
 * The same fragment uncompressed (version 0), made at once and without the
 * browser's streams: for links a page writes out from data, in the app and
 * the build alike.
 */
export function plainShareFragment(state) {
  return `${PREFIX}0${toBase64Url(new TextEncoder().encode(JSON.stringify(state)))}`;
}

export function isShareFragment(fragment) {
  return typeof fragment === 'string' && fragment.startsWith(PREFIX);
}

/**
 * State object from a fragment, or null when it is missing or malformed.
 * Only string and boolean values are accepted.
 */
export async function decodeShareFragment(fragment) {
  if (!isShareFragment(fragment)) return null;
  try {
    const version = fragment[PREFIX.length];
    let bytes = fromBase64Url(fragment.slice(PREFIX.length + 1));
    if (version === '1') bytes = await pipeThrough(bytes, new DecompressionStream('deflate-raw'));
    else if (version !== '0') return null;
    const data = JSON.parse(new TextDecoder().decode(bytes));
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    const state = {};
    for (const [id, value] of Object.entries(data)) {
      if (typeof value === 'string' || typeof value === 'boolean') state[id] = value;
    }
    return Object.keys(state).length ? state : null;
  } catch {
    return null;
  }
}
